import {
  ApiServiceType,
  BaseService,
  BlobWithHeaders,
  transformBlobResponse,
} from '@cbridge-packages/api-base';
import { AxiosObservable } from '@cbridge-utils/axios-observable';
import { IApiGetReportsReq, IApiGetReportsRes } from '../_interfaces/IApiGetReports';
import { IApiGetReportDataReq, IApiGetReportDataRes } from '../_interfaces/IApiGetReportData';
import { IApiDownloadReportDataReq } from '../_interfaces/IApiDownloadReportData';
import {
  IApiGetAccountSymbolsReq,
  IApiGetAccountSymbolsRes,
} from '../_interfaces/IApiGetAccountSymbols';
import {
  IApiCreateReportReq,
  IApiCreateReportRes,
} from '../_interfaces/IApiCreateReport';
import {
  IApiGetFilterAccountGroupsRes,
  IApiGetFilterAccountsRes,
  IApiGetFilterServersRes,
  IApiGetFilterSymbolsRes,
} from '../_interfaces/IApiGetFilterOptions';
import { toApiQuery } from '../_helpers/toApiQuery';

export class MarkoutApiService extends BaseService {
  constructor() {
    super(ApiServiceType.Markout);
  }

  getReports(payload?: IApiGetReportsReq): AxiosObservable<IApiGetReportsRes> {
    return this.api.get<IApiGetReportsRes>('/reports', { params: payload });
  }

  getDashboardData(payload?: IApiGetReportDataReq): AxiosObservable<IApiGetReportDataRes> {
    return this.api.get<IApiGetReportDataRes>('/dashboard', { params: toApiQuery(payload) });
  }

  getReportData(
    reportId: number | string,
    jobId: number | string,
    payload?: IApiGetReportDataReq,
  ): AxiosObservable<IApiGetReportDataRes> {
    return this.api.get<IApiGetReportDataRes>(
      `/reports/${reportId}/jobs/${jobId}/data`,
      { params: toApiQuery(payload) },
    );
  }

  downloadReportData(
    reportId: number | string,
    jobId: number | string,
    payload?: IApiDownloadReportDataReq,
  ): AxiosObservable<BlobWithHeaders> {
    return this.api.get<BlobWithHeaders>(
      `/reports/${reportId}/jobs/${jobId}/data/export`,
      {
        params: payload,
        responseType: 'blob',
        transformResponse: transformBlobResponse,
      },
    );
  }

  getReportAccountSymbols(
    reportId: number | string,
    jobId: number | string,
    serverId: string,
    accountId: string,
    payload?: IApiGetAccountSymbolsReq,
  ): AxiosObservable<IApiGetAccountSymbolsRes> {
    return this.api.get<IApiGetAccountSymbolsRes>(
      `/reports/${reportId}/jobs/${jobId}/data/${serverId}/accounts/${accountId}`,
      { params: toApiQuery(payload) },
    );
  }

  getDashboardAccountSymbols(
    serverId: string,
    accountId: string,
    payload?: IApiGetAccountSymbolsReq,
  ): AxiosObservable<IApiGetAccountSymbolsRes> {
    return this.api.get<IApiGetAccountSymbolsRes>(
      `/dashboard/${serverId}/accounts/${accountId}`,
      { params: toApiQuery(payload) },
    );
  }

  getReportServers(
    reportId: number | string,
    jobId: number | string,
  ): AxiosObservable<IApiGetFilterServersRes> {
    return this.api.get<IApiGetFilterServersRes>(`/reports/${reportId}/jobs/${jobId}/servers`);
  }

  getReportAccounts(
    reportId: number | string,
    jobId: number | string,
  ): AxiosObservable<IApiGetFilterAccountsRes> {
    return this.api.get<IApiGetFilterAccountsRes>(`/reports/${reportId}/jobs/${jobId}/accounts`);
  }

  getReportAccountGroups(
    reportId: number | string,
    jobId: number | string,
  ): AxiosObservable<IApiGetFilterAccountGroupsRes> {
    return this.api.get<IApiGetFilterAccountGroupsRes>(`/reports/${reportId}/jobs/${jobId}/account-groups`);
  }

  getReportSymbols(
    reportId: number | string,
    jobId: number | string,
  ): AxiosObservable<IApiGetFilterSymbolsRes> {
    return this.api.get<IApiGetFilterSymbolsRes>(`/reports/${reportId}/jobs/${jobId}/symbols`);
  }

  getDashboardServers(): AxiosObservable<IApiGetFilterServersRes> {
    return this.api.get<IApiGetFilterServersRes>('/dashboard/servers');
  }

  getDashboardAccounts(): AxiosObservable<IApiGetFilterAccountsRes> {
    return this.api.get<IApiGetFilterAccountsRes>('/dashboard/accounts');
  }

  getDashboardAccountGroups(): AxiosObservable<IApiGetFilterAccountGroupsRes> {
    return this.api.get<IApiGetFilterAccountGroupsRes>('/dashboard/account-groups');
  }

  getDashboardSymbols(): AxiosObservable<IApiGetFilterSymbolsRes> {
    return this.api.get<IApiGetFilterSymbolsRes>('/dashboard/symbols');
  }

  createReport(payload: IApiCreateReportReq): AxiosObservable<IApiCreateReportRes> {
    return this.api.post<IApiCreateReportRes>('/reports', payload);
  }

  stopReport(reportId: number | string): AxiosObservable<void> {
    return this.api.post<void>(`/reports/${reportId}/stop`);
  }

  retryReport(reportId: number | string): AxiosObservable<void> {
    return this.api.post<void>(`/reports/${reportId}/retry`);
  }
}
